import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertCircle, AlertTriangle, ArrowLeft, ArrowRight, CheckCircle,
  CheckSquare, ClipboardCheck, Clock, LucideIcon, Package, Shield, Thermometer,
  Wrench, X,
} from 'lucide-react';
import TimesheetEditManager from '../../managers/TimesheetEditManager';
import {
  PayrollSubmitTimesheetRequest,
  PayrollTimesheetStateEditable,
} from '../../interfaces/TimesheetEditInterface';
import { Day } from '../../interfaces/EmployeeInterface';
import { TimeEntryType } from '../../interfaces/TimesheetInterface';
import { formatHours } from '../../util/hoursFormat';
import './PayrollSubmitWizard.css';

/**
 * Submit-for-employee wizard (Payroll Timesheet Modifications, M1-B3 — the
 * accepted design v2's wizard-only submit modal, `design_handoff_payroll_
 * timesheet_edit_design_v2` shots 10/11 + iteration-1 state 9 for the 409).
 *
 * Steps: Review receipt (the EXACT ID set being submitted, per kind — the API
 * rejects the submit unless each array exactly matches its unlinked rows) →
 * Attestation + confirm. Success is handed to the caller (popup + board
 * refresh); the 409 conflict renders in-wizard with the iteration-1 banner
 * design and recovers by re-reading `/state` for the found timesheetID.
 *
 * PAYROLL DOES NOT ANSWER THE ATTESTATION QUESTIONS. The design's original
 * Questions step is gone: attesting is the employee's act, and a payroll clerk
 * cannot truthfully answer "did you take your meal break" on their behalf. The
 * submit posts an empty `timesheetAnswers` array, which the API already accepts
 * as a no-op (controllers/admin/timesheetEdit.ts `?? []` → an empty insert
 * loop), so no server change is needed. A tenant's questions are still asked of
 * the employee on every self-submit; only this payroll path skips them.
 */

export interface PayrollSubmitWizardProps {
  /** The editable state the drill-down loaded — questions, counts, day collections. */
  state: PayrollTimesheetStateEditable;
  payPeriodID: number;
  /** "Aug 3 – 9, 2026"-style label for copy. */
  payPeriodLabel: string;
  /** Cancel / close without submitting (steps view only). */
  onClose: () => void;
  /** Submit landed — the caller owns the success popup + board refresh + navigation. */
  onSuccess: (timesheetID?: number) => void;
  /** 409 primary action: open the week the employee submitted mid-flow. */
  onOpenSubmittedWeek: (timesheetID: number | null) => void;
  /** 409 secondary action: back to the board (refreshed). */
  onBackToBoard: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function genEditSessionID(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && 'randomUUID' in c) return (c as { randomUUID(): string }).randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function money(v: number): string {
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Worked minutes for one punch. An open punch (no out) contributes 0, and so
 * does a MEAL punch — an unpaid break is not worked time. This mirrors the
 * server, which excludes meals in `timesheetsUtil._calculateTotalMinutes` /
 * `_calculateTotalMinutesFromHourlyProjects` and in the hour calculator itself
 * (`CAWeekCalc` only accumulates `TimeEntryType.Regular`). Without the meal
 * check the receipt's per-kind hours overstated the day by every break on it,
 * while the "Week total" line beneath — which comes from the server's
 * `totalWorkMinutes` — read correctly, so the receipt disagreed with itself.
 *
 * Meal punches are still SUBMITTED: their IDs stay in the ID arrays, because
 * the API rejects the submit unless each array exactly matches its unlinked
 * rows. Only the displayed hours change.
 */
function punchMinutes(entry: { type?: number; inDateTime: string; outDateTime?: string | null }): number {
  if (entry.type === TimeEntryType.Meal) return 0;
  if (!entry.inDateTime || !entry.outDateTime) return 0;
  const mins = Math.round((Date.parse(entry.outDateTime) - Date.parse(entry.inDateTime)) / 60000);
  return Number.isFinite(mins) && mins > 0 ? mins : 0;
}

// Entry-kind vocabulary — labels/sections from the design bundle's kind registry;
// colors are the v2 tokens with backgrounds precomputed to hex (house convention).
interface KindMeta { label: string; section: string; color: string; bg: string; Icon: LucideIcon; }
const KIND_META: Record<string, KindMeta> = {
  hourly:      { label: 'Hourly',      section: 'Hourly punches',    color: '#1f9d6b', bg: '#e6f4ee', Icon: Clock },
  piece:       { label: 'Piece',       section: 'Piece punches',     color: '#2f6fed', bg: '#e8effd', Icon: Package },
  task:        { label: 'Task',        section: 'Task claims',       color: '#2f6fed', bg: '#e8effd', Icon: CheckSquare },
  misc:        { label: 'Misc',        section: 'Misc tasks',        color: '#d8852f', bg: '#faf1e8', Icon: Wrench },
  csw:         { label: 'CSW',         section: 'CSW tasks',         color: '#b1499c', bg: '#f6ebf4', Icon: Shield },
  sick:        { label: 'Sick',        section: 'Sick claims',       color: '#2fa3c2', bg: '#e8f4f8', Icon: Thermometer },
  workOrder:   { label: 'Work order',  section: 'Work order claims', color: '#7a5cf0', bg: '#f0edfd', Icon: ClipboardCheck },
  operational: { label: 'Operational', section: 'Operational tasks', color: '#6b7686', bg: '#eff1f3', Icon: Activity },
};

interface ReceiptRow { kind: string; count: number; value: string; }

/** Everything the submit sends, gathered once from the state's day collections. */
interface SubmissionSet {
  timeEntryIDs: number[];
  timeEntryHourlyIDs: number[];
  employeeTaskIDs: number[];
  miscEmployeeTaskIDs: number[];
  employeeCustomerServiceWarrantyTaskIDs: number[];
  employeeSickClaimIDs: number[];
  employeeOperationalTaskIDs: number[];
  employeeWorkOrderTaskIDs: number[];
  rows: ReceiptRow[];
  workedMinutes: number;
  claimedAmount: number;
  total: number;
}

function collectSubmission(days: Day[]): SubmissionSet {
  const s: SubmissionSet = {
    timeEntryIDs: [], timeEntryHourlyIDs: [], employeeTaskIDs: [],
    miscEmployeeTaskIDs: [], employeeCustomerServiceWarrantyTaskIDs: [],
    employeeSickClaimIDs: [], employeeOperationalTaskIDs: [], employeeWorkOrderTaskIDs: [],
    rows: [], workedMinutes: 0, claimedAmount: 0, total: 0,
  };
  let hourlyMin = 0, pieceMin = 0, taskAmt = 0, miscAmt = 0, cswAmt = 0,
    sickMin = 0, opMin = 0, woAmt = 0;
  days.forEach((d) => {
    s.workedMinutes += d.totalWorkMinutes ?? 0;
    s.claimedAmount += d.totalClaimedAmount ?? 0;
    (d.timeEntries ?? []).forEach((te) => {
      s.timeEntryIDs.push(te.timeEntryID);
      pieceMin += punchMinutes(te);
    });
    (d.hourlyProjects ?? []).forEach((hp) => (hp.timeEntriesHourly ?? []).forEach((te) => {
      s.timeEntryHourlyIDs.push(te.timeEntryHourlyID);
      hourlyMin += punchMinutes(te);
    }));
    (d.projectEmployeeTasks ?? []).forEach((pet) => {
      (pet.employeeTasks ?? []).forEach((t) => { s.employeeTaskIDs.push(t.employeeTaskID); taskAmt += t.amountClaimed ?? 0; });
      (pet.miscEmployeeTasks ?? []).forEach((t) => { s.miscEmployeeTaskIDs.push(t.miscEmployeeTaskID); miscAmt += t.amountClaimed ?? 0; });
    });
    (d.employeeCustomerServiceWarrantyTasks ?? []).forEach((t) => {
      s.employeeCustomerServiceWarrantyTaskIDs.push(t.employeeCustomerServiceWarrantyID);
      cswAmt += t.amountClaimed ?? 0;
    });
    (d.employeeSickClaims ?? []).forEach((t) => {
      s.employeeSickClaimIDs.push(t.employeeSickClaimID);
      sickMin += t.minutesClaimed ?? 0;
    });
    (d.employeeOperationalTasks ?? []).forEach((t) => {
      s.employeeOperationalTaskIDs.push(t.employeeOperationalTaskID);
      opMin += t.minutesClaimed ?? 0;
    });
    (d.employeeWorkOrderTasks ?? []).forEach((t) => {
      s.employeeWorkOrderTaskIDs.push(t.employeeWorkOrderTaskID);
      woAmt += t.amountImplied ?? 0;
    });
  });
  const push = (kind: string, count: number, value: string) => {
    if (count > 0) s.rows.push({ kind, count, value });
  };
  push('hourly', s.timeEntryHourlyIDs.length, formatHours(hourlyMin));
  push('piece', s.timeEntryIDs.length, formatHours(pieceMin));
  push('task', s.employeeTaskIDs.length, money(taskAmt));
  push('misc', s.miscEmployeeTaskIDs.length, money(miscAmt));
  push('csw', s.employeeCustomerServiceWarrantyTaskIDs.length, money(cswAmt));
  push('sick', s.employeeSickClaimIDs.length, formatHours(sickMin));
  push('operational', s.employeeOperationalTaskIDs.length, formatHours(opMin));
  push('workOrder', s.employeeWorkOrderTaskIDs.length, money(woAmt));
  s.total = s.rows.reduce((n, r) => n + r.count, 0);
  return s;
}

const kindChip = (kind: string) => {
  const K = KIND_META[kind];
  if (!K) return null;
  const Icon = K.Icon;
  return (
    <span className="pe-kind" style={{ color: K.color, background: K.bg }}>
      <Icon />
      <span className="kl">{K.label}</span>
    </span>
  );
};

interface Banner { kind: 'danger' | 'warn' | 'info'; title: string; body: string; }

// ── Component ────────────────────────────────────────────────────────────────

const PayrollSubmitWizard: React.FC<PayrollSubmitWizardProps> = ({
  state, payPeriodID, payPeriodLabel, onClose, onSuccess, onOpenSubmittedWeek, onBackToBoard,
}) => {
  // The working copy of the state — replaced when a stale-400 forces a refresh,
  // so the receipt always shows exactly what the next submit will send.
  const [wizState, setWizState] = useState<PayrollTimesheetStateEditable>(state);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [view, setView] = useState<'steps' | 'conflict'>('steps');
  const [conflictTimesheetID, setConflictTimesheetID] = useState<number | null>(null);

  // One GUID per sitting (§6.1) — ties this submit to its change-log row.
  const editSessionID = useMemo(genEditSessionID, []);

  const firstName = wizState.name?.first ?? '';
  const fullName = `${wizState.name?.first ?? ''} ${wizState.name?.last ?? ''}`.trim();

  // Two steps, always — payroll does not answer the tenant's questions.
  const stepLabels = useMemo(() => ['Review', 'Confirm'], []);
  const reviewStepIdx = 0;
  const lastStepIdx = stepLabels.length - 1;

  const submission = useMemo(() => collectSubmission(wizState.days ?? []), [wizState]);

  // Esc: cancel (steps) / back to the board (conflict) — never while submitting.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' || submitting) return;
      if (view === 'conflict') onBackToBoard(); else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, submitting, onClose, onBackToBoard]);

  const enterConflict = async () => {
    setView('conflict');
    setConflictTimesheetID(null);
    // The 409 body carries no timesheetID (§7.4) — re-read /state to name it.
    try {
      const s = await TimesheetEditManager.loadState(wizState.employeeID, payPeriodID);
      if (s.exists) setConflictTimesheetID(s.timesheetID);
    } catch {
      // Leave the ID unnamed; the primary action still routes to the submitted week.
    }
  };

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setBanner(null);
    const request: PayrollSubmitTimesheetRequest = {
      employeeID: wizState.employeeID,
      payPeriodID,
      // Always empty: payroll does not attest on the employee's behalf.
      timesheetAnswers: [],
      timeEntryIDs: submission.timeEntryIDs,
      timeEntryHourlyIDs: submission.timeEntryHourlyIDs,
      employeeTaskIDs: submission.employeeTaskIDs,
      miscEmployeeTaskIDs: submission.miscEmployeeTaskIDs,
      employeeCustomerServiceWarrantyTaskIDs: submission.employeeCustomerServiceWarrantyTaskIDs,
      employeeSickClaimIDs: submission.employeeSickClaimIDs,
      ...(submission.employeeOperationalTaskIDs.length > 0
        ? { employeeOperationalTaskIDs: submission.employeeOperationalTaskIDs } : {}),
      ...(submission.employeeWorkOrderTaskIDs.length > 0
        ? { employeeWorkOrderTaskIDs: submission.employeeWorkOrderTaskIDs } : {}),
      editSessionID,
    };
    try {
      const result = await TimesheetEditManager.submitForEmployee(request);
      onSuccess(result.timesheetID);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      const status = e?.response?.status;
      const message = e?.response?.data?.message ?? '';
      if (status === 409) {
        await enterConflict();
      } else if (status === 400) {
        // Stale ID arrays — the week changed under us. Refresh the state and
        // land back on the review step so the receipt is again a true preview.
        try {
          const s = await TimesheetEditManager.loadState(wizState.employeeID, payPeriodID);
          if (s.exists) {
            setConflictTimesheetID(s.timesheetID);
            setView('conflict');
          } else {
            setWizState(s);
            setStep(reviewStepIdx);
            setBanner({
              kind: 'info',
              title: 'This week changed while you were working.',
              body: message || 'The list below has been refreshed — it is exactly what will be submitted.',
            });
          }
        } catch {
          setBanner({
            kind: 'danger',
            title: 'Could not refresh the week.',
            body: message || 'Please close and reopen the submit window.',
          });
        }
      } else {
        setBanner({
          kind: 'danger',
          title: status === 403 ? 'Not permitted' : 'Submit failed',
          body: message || 'Could not submit the timesheet. Please try again.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step bodies ─────────────────────────────────────────────────────────────

  const stepReview = (
    <div className="pe-msect">
      <h4>Everything being submitted</h4>
      <p className="lede">
        This is the exact set of entries sent — the server rejects the submit if it no longer
        matches.
      </p>
      <div className="pe-receipt">
        {submission.rows.map((r) => (
          <div key={r.kind} className="pe-rrow">
            {kindChip(r.kind)}
            <span className="grow">{KIND_META[r.kind]?.section ?? r.kind}</span>
            <span className="c">{r.count}</span>
            <span className="v">{r.value}</span>
          </div>
        ))}
        <div className="pe-rrow">
          <span className="grow">Week total</span>
          <span className="c">{formatHours(submission.workedMinutes)} worked</span>
          <span className="v">{money(submission.claimedAmount)}</span>
        </div>
      </div>
    </div>
  );

  const stepAttest = (
    <div className="pe-msect">
      <h4>Recorded submission</h4>
      <div className="pe-attest">
        <Shield />
        <div>
          You are submitting <b>{fullName}&rsquo;s</b> timesheet for <b>{payPeriodLabel}</b> as
          payroll.
          <ul>
            <li>
              The submission is stamped with <b>your name</b> and the server time, and kept in the
              change history for this pay period.
            </li>
            <li>
              The employee&rsquo;s signature is recorded as <b>submitted by payroll</b>; the printed
              timecard shows &ldquo;Unavailable to sign&rdquo;.
            </li>
            <li>
              The attestation questions are <b>not answered</b> — payroll does not attest on an
              employee&rsquo;s behalf, so this timesheet is submitted with no answers recorded.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const stepBodies = [stepReview, stepAttest];

  const conflictBody = (
    <div className="pe-msect">
      <div className="pe-ban warn">
        <AlertTriangle />
        <div className="grow">
          <b>{firstName} submitted from the app while you were working.</b>
          This week now has a timesheet, so your submit was <b>not applied</b>. Nothing on the
          timesheet itself was changed or lost.
        </div>
      </div>
      <p className="pe-note">
        {conflictTimesheetID != null
          ? <>Reloading found timesheet <span className="mono">#{conflictTimesheetID}</span> for {payPeriodLabel}.</>
          : <>Reloading the submitted week…</>}
      </p>
      <div className="pe-conflict-actions">
        <button
          className="pe-btn primary"
          onClick={() => onOpenSubmittedWeek(conflictTimesheetID)}
          type="button"
        >
          <ArrowRight /> Open the submitted week
        </button>
        <button className="pe-btn" onClick={onBackToBoard} type="button">
          Back to the board
        </button>
      </div>
    </div>
  );

  // ── Frame ───────────────────────────────────────────────────────────────────

  const stepsHeader = (
    <div className="pe-steps">
      {stepLabels.map((label, i) => (
        <React.Fragment key={label}>
          <span className={`pe-step${i === step ? ' on' : ''}${i < step ? ' done' : ''}`}>
            <span className="n">{i < step ? '✓' : i + 1}</span>
            {label}
          </span>
          {i < lastStepIdx && <span className="bar" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="pe-mwrap open" role="dialog" aria-modal="true" aria-label={`Submit for ${fullName}`}>
      <div className="pe-modal">
        <div className="pe-mhead">
          <span className="mi"><CheckCircle /></span>
          <div className="grow">
            <h3>Submit for {fullName}</h3>
            <div className="sub">{payPeriodLabel} · #{wizState.altEmployeeID}</div>
          </div>
          <button
            className="pe-iact"
            onClick={view === 'conflict' ? onBackToBoard : onClose}
            disabled={submitting}
            title="Close"
            type="button"
          >
            <X />
          </button>
        </div>
        <div className="pe-mbody">
          {view === 'conflict' ? conflictBody : (
            <>
              {stepsHeader}
              {banner && (
                <div className="pe-msect pe-msect-banner">
                  <div className={`pe-ban ${banner.kind}`}>
                    {banner.kind === 'danger' ? <AlertCircle /> : banner.kind === 'warn' ? <AlertTriangle /> : <AlertCircle />}
                    <div className="grow">
                      <b>{banner.title}</b>
                      {banner.body}
                    </div>
                  </div>
                </div>
              )}
              {stepBodies[Math.min(step, lastStepIdx)]}
            </>
          )}
        </div>
        {view === 'steps' && (
          <div className="pe-mfoot">
            {step > 0 ? (
              <button
                className="pe-btn"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={submitting}
                type="button"
              >
                <ArrowLeft /> Back
              </button>
            ) : (
              <button className="pe-btn" onClick={onClose} disabled={submitting} type="button">
                Cancel
              </button>
            )}
            <span className="grow" />
            {step < lastStepIdx ? (
              <button
                className="pe-btn primary"
                onClick={() => setStep((s) => Math.min(lastStepIdx, s + 1))}
                type="button"
              >
                Next <ArrowRight />
              </button>
            ) : (
              <button
                className="pe-btn primary"
                onClick={handleConfirm}
                disabled={submitting}
                type="button"
              >
                <CheckCircle /> {submitting ? 'Submitting…' : `Submit for ${firstName}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollSubmitWizard;
